/**
 * POSITION-KEYED RNG FORKS. Owned by `world`.
 *
 * THE PROBLEM THIS SOLVES. Block layout, lot subdivision and street props were all drawn from
 * ONE sequential rng stream, walked in index order. That is deterministic — N3 is satisfied —
 * but it is not STABLE: insert one street at the west end and every block after it draws
 * somebody else's numbers, so the entire city re-rolls. The phase-1 expansion prepends three
 * rues, which shifts every north-south index by three, so without this the Plateau core would
 * have been rebuilt from scratch: different lot widths, different families, different storey
 * counts, different shops, in eight graded shots that have eleven rounds of tuning sitting on
 * them.
 *
 * THE FIX. Fork on the thing that did not move — the WORLD COORDINATE. `Rng.fork(label)` is
 * derived from (seed, label) and is order-independent (§6), so a block keyed on its own
 * position draws the same numbers no matter how many blocks were created before it. The old
 * core is bit-identical across the expansion, and every future phase (Old Port, Mont Royal)
 * is likewise non-destructive.
 *
 * WHY QUANTISE. Coordinates are floats and `nsX[i] + nsWalk[i]` is a sum, so the same block
 * edge can differ in the last bit between builds or engines. Keys are therefore quantised to
 * `Q` metres before being stringified. Q is far larger than any float error and far smaller
 * than the spacing between two distinct blocks, lots or street lines, so the mapping is
 * injective in practice and stable everywhere.
 *
 * COST. One `fork()` per block, per lot and per street line — a few hundred at build time,
 * none per frame (N9). `fork` hashes a short string and seeds four lanes; it is nothing next
 * to the geometry it gates.
 */

/** Quantisation, metres. Nothing in the city places two keyed features closer than this. */
const Q = 0.05;

function q(v) {
  // `| 0` after rounding keeps the label an integer literal rather than `-0` or `1e-7`.
  const n = Math.round(v / Q) | 0;
  return n === 0 ? '0' : String(n);
}

/**
 * A stable label for a feature at (x, z).
 * @param {string} kind  namespace — 'block', 'lot', 'nsLine', … Keep distinct kinds distinct,
 *                       or a lot and the block it sits in would share a stream.
 */
export function posKey(kind, x, z) {
  return kind + '@' + q(x) + ',' + q(z);
}

/** A stable label for a feature on one axis only (a street line, a ruelle). */
export function linePos(kind, axis, pos) {
  return kind + '@' + axis + ':' + q(pos);
}
